import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const handleLogoClick = (e) => {
    // If already on home page, scroll to top smoothly
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.products'), path: '/products' },
    { name: t('nav.contact'), path: '/contact' },
    { name: t('nav.enquiry'), path: '/enquiry' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" onClick={handleLogoClick}>
            <img 
              src="https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/mwmdalj9_Dharani%20Pipe%20Logo.png" 
              alt="Dharani Pipes" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-colors duration-200 px-2 py-1 ${
                  isActive(link.path)
                    ? 'text-[#4A5F8F] border-b-2 border-[#4A5F8F]'
                    : 'text-gray-700 hover:text-[#4A5F8F]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* Small Employee Login Link */}
            <a
              href="https://accounts.zoho.com/signin?servicename=VirtualOffice&signupurl=https://www.zoho.com/mail/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#4A5F8F] transition-colors duration-200"
              title="Employee Login"
            >
              <LogIn size={14} />
              <span>{t('nav.staff')}</span>
            </a>
          </div>

          {/* Mobile: Language Switcher + Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Language Switcher for Mobile Header */}
            <LanguageSwitcher isMobile={true} />
            
            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md text-gray-700 hover:text-[#4A5F8F] hover:bg-gray-100 transition-colors duration-200"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 text-base font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-[#4A5F8F] bg-blue-50'
                    : 'text-gray-700 hover:text-[#4A5F8F] hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Small Employee Login for Mobile */}
            <a
              href="https://accounts.zoho.com/signin?servicename=VirtualOffice&signupurl=https://www.zoho.com/mail/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-xs text-gray-500 hover:text-[#4A5F8F] transition-colors duration-200 text-center border-t border-gray-200 mt-2"
            >
              <LogIn size={14} className="inline mr-1" />
              {t('nav.staff')} Login
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
